import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../assets/logo.png";

const INNER_HERO_PATHS = [
  "/book",
  "/packages",
  "/rentals",
  "/gallery",
  "/blog",
  "/printing",
  "/shop",
  "/contact",
  "/checkout",
  "/portrait-photographer-rochdale",
  "/family-photographer-rochdale",
  "/photography-studio-rochdale",
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState(73);
  const headerRef = useRef(null);
  const location = useLocation();

  const isHome = location.pathname === "/";
  const hasInnerHero = INNER_HERO_PATHS.includes(location.pathname);
  // Homepage keeps its original behaviour: transparent until scrolled.
  const hasDarkNavbar = isHome
    ? scrolled
    : !hasInnerHero || scrolled || open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const h = headerRef.current.offsetHeight;
        setNavbarHeight(h);
        document.documentElement.style.setProperty("--header-h", h + "px");
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const isShopActive = location.pathname.startsWith("/shop") ||
    location.pathname.startsWith("/cart") ||
    location.pathname.startsWith("/checkout");

  const isBlogActive = location.pathname.startsWith("/blog");

  const isPathActive = (path) =>
    path === "/shop"
      ? isShopActive
      : path === "/blog"
        ? isBlogActive
        : location.pathname === path;

  const linkClass = (path) => {
    const isActive = isPathActive(path);

    return `relative text-[10px] lg:text-xs tracking-[0.06em] lg:tracking-[0.1em] uppercase whitespace-nowrap transition-all duration-200 ${
      isActive
        ? "text-white after:absolute after:left-0 after:-bottom-2 after:h-[2px] after:w-full after:bg-pink-500"
        : "text-white/90 hover:text-pink-300"
    }`;
  };

  const mobileLinkClass = (path) => {
    const isActive = isPathActive(path);

    return `relative block w-fit py-2.5 text-sm tracking-[0.1em] uppercase transition-all duration-200 ${
      isActive
        ? "text-white after:absolute after:left-0 after:-bottom-[2px] after:h-[2px] after:w-full after:bg-pink-500"
        : "text-white/90 hover:text-pink-300"
    }`;
  };

  return (
    <>
      <header
        ref={headerRef}
        className={[
          "fixed left-0 right-0 top-0 z-50",
          "transition-all duration-300 ease-out",
          hasDarkNavbar
            ? "border-b border-transparent bg-[#1c1c1c]/95 backdrop-blur"
            : "border-b border-transparent bg-transparent",
        ].join(" ")}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10 lg:px-16 lg:py-5 xl:px-10">
          <Link to="/" className="flex items-center shrink-0">
            <img
              src={logo}
              alt="Kinews Studio"
              className="h-auto w-40 lg:w-52 xl:w-60"
            />
          </Link>

          <nav className="hidden items-center gap-4 lg:gap-5 xl:gap-7 xl:flex">
            <NavLink to="/packages" className={() => linkClass("/packages")}>
              Event Packages
            </NavLink>

            <NavLink to="/rentals" className={() => linkClass("/rentals")}>
              Studio Rentals
            </NavLink>

            <NavLink to="/gallery" className={() => linkClass("/gallery")}>
              Gallery
            </NavLink>
            <NavLink to="/printing" className={() => linkClass("/printing")}>
              Prints
            </NavLink>

            <NavLink to="/shop" className={() => linkClass("/shop")}>
              Shop
            </NavLink>

            <NavLink to="/blog" className={() => linkClass("/blog")}>
              Blog
            </NavLink>

            <NavLink to="/contact" className={() => linkClass("/contact")}>
              Contact
            </NavLink>
          </nav>

          <Link
            to="/book"
            className="hidden shrink-0 xl:inline-flex rounded-full bg-pink-500 px-4 py-2.5 lg:px-6 text-[10px] lg:text-xs font-medium uppercase tracking-[0.06em] lg:tracking-[0.08em] text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-pink-600"
          >
            Book Session
          </Link>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="relative flex h-10 w-10 items-center justify-center rounded-full border border-pink-500/25 bg-black/40 xl:hidden"
          >
            <span className="sr-only">Menu</span>

            <span
              className={`absolute h-[2px] w-5 bg-white transition ${
                open ? "rotate-45" : "-translate-y-2"
              }`}
            />
            <span
              className={`absolute h-[2px] w-5 bg-white transition ${
                open ? "opacity-0" : ""
              }`}
            />
            <span
              className={`absolute h-[2px] w-5 bg-white transition ${
                open ? "-rotate-45" : "translate-y-2"
              }`}
            />
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              className="fixed left-0 right-0 z-50 bg-[#1c1c1c] xl:hidden"
              style={{ top: navbarHeight }}
              initial={{ y: -12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -12, opacity: 0 }}
            >
              <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16 xl:px-10 py-5">
                <div className="space-y-0.5">
                  <NavLink
                    to="/packages"
                    className={() => mobileLinkClass("/packages")}
                  >
                    Event Packages
                  </NavLink>

                  <NavLink
                    to="/rentals"
                    className={() => mobileLinkClass("/rentals")}
                  >
                    Studio Rentals
                  </NavLink>

                  <NavLink
                    to="/gallery"
                    className={() => mobileLinkClass("/gallery")}
                  >
                    Gallery
                  </NavLink>
                  <NavLink
                    to="/printing"
                    className={() => mobileLinkClass("/printing")}
                  >
                    Prints
                  </NavLink>

                  <NavLink
                    to="/shop"
                    className={() => mobileLinkClass("/shop")}
                  >
                    Shop
                  </NavLink>

                  <NavLink
                    to="/blog"
                    className={() => mobileLinkClass("/blog")}
                  >
                    Blog
                  </NavLink>

                  <NavLink
                    to="/contact"
                    className={() => mobileLinkClass("/contact")}
                  >
                    Contact
                  </NavLink>
                </div>

                <div className="mt-5">
                  <Link
                    to="/book"
                    className="inline-flex w-full items-center justify-center rounded-full bg-pink-500 px-6 py-3 text-sm font-medium uppercase tracking-[0.1em] text-white"
                  >
                    Book Session
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Blur overlay portaled to document.body to escape header's stacking context */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              className="fixed inset-x-0 bottom-0 z-40 bg-black/40 backdrop-blur-md"
              style={{ top: navbarHeight }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
